import { useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { AddContractorDialog } from '../../people/components/AddContractorDialog';
import { peopleGateway } from '../../people';
import type { ContractorResponse } from '../../people/types';
import { ACCEPT_ATTR, checkSupportingDocument } from '../fileCheck';
import { METHOD_LABEL, formatBytes, todayIso } from '../format';
import { PAYMENT_METHODS, type OutsidePaymentInput, type PaymentMethod } from '../types';
import { CalendarIcon, CaretDownIcon, FileIcon, TrashIcon, UploadIcon } from './icons';

const MONEY = /^\d{1,13}(\.\d{1,2})?$/;
const NINE_DIGITS = /\d{9,}/;
const CHECK_NUMBER = /^[A-Za-z0-9-]{1,20}$/;

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'loaded'; contractors: ContractorResponse[] };
type Doc = { file: File; state: 'checking' | 'ready' | 'invalid' | 'uploading' | 'failed'; message: string | null };
type Field = 'contractor' | 'paymentDate' | 'method' | 'serviceStart' | 'serviceEnd' | 'amount' | 'memo' | 'reference' | 'checkNumber';

/** What the dialog returns to the page: a plain-language refusal (with whether it is the duplicate guard), or null once the record is saved. */
export type OutsideSubmitResult = { message: string; duplicate: boolean } | null;

/**
 * Record Outside Payment (Figma S02-S04): an admin records a payment that was ALREADY made outside Escochex. It moves no money and needs no approver; it
 * creates a record directly as Paid. The contractor is chosen from this company's contractors (never free-typed); one that doesn't exist yet can be added
 * in place, over this dialog. The supporting document travels with the save (one multipart request), so the file chip never claims "uploaded" before the
 * server has stored it: it reads "Ready" until Save, "Uploading" while saving and "Not uploaded" when the save failed (values and file are kept).
 */
export function RecordOutsidePaymentDialog({ onClose, onSubmit }: { onClose: () => void; onSubmit: (input: OutsidePaymentInput) => Promise<OutsideSubmitResult> }) {
  const uid = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [contractorId, setContractorId] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [method, setMethod] = useState<'' | PaymentMethod>('');
  const [serviceStart, setServiceStart] = useState('');
  const [serviceEnd, setServiceEnd] = useState('');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [reference, setReference] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [trackable, setTrackable] = useState(false);
  const [trackableTouched, setTrackableTouched] = useState(false);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [touched, setTouched] = useState<ReadonlySet<Field>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; duplicate: boolean } | null>(null);
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    peopleGateway
      .listContractors({ page: 1, pageSize: 100 })
      .then((page) => { if (!cancelled) setLoad({ kind: 'loaded', contractors: page.items }); })
      .catch(() => { if (!cancelled) setLoad({ kind: 'error' }); });
    return () => { cancelled = true; };
  }, []);

  const contractors = useMemo(() => (load.kind === 'loaded' ? load.contractors : []), [load]);
  const selected = contractors.find((c) => c.id === contractorId) ?? null;

  const errors = useMemo(() => {
    const e: Partial<Record<Field, string>> = {};
    if (!contractorId) e.contractor = 'Choose a contractor.';
    if (!paymentDate) e.paymentDate = 'Enter the payment date.';
    else if (paymentDate > todayIso()) e.paymentDate = 'The payment date can’t be in the future.';
    else if (paymentDate < '2000-01-01') e.paymentDate = 'Enter a date from the year 2000 on.';
    if (!method) e.method = 'Choose a payment method.';
    if (!serviceStart) e.serviceStart = 'Enter the service period start.';
    if (!serviceEnd) e.serviceEnd = 'Enter the service period end.';
    else if (serviceStart && serviceEnd < serviceStart) e.serviceEnd = 'The end can’t be before the start.';
    if (!MONEY.test(amount.trim()) || Number(amount) <= 0) e.amount = 'Enter an amount greater than zero, with at most two decimals.';
    if (memo.length > 500) e.memo = 'Notes must be 500 characters or fewer.';
    else if (NINE_DIGITS.test(memo)) e.memo = 'Don’t enter account or routing numbers in the notes.';
    if (reference.length > 100) e.reference = 'The reference must be 100 characters or fewer.';
    else if (NINE_DIGITS.test(reference)) e.reference = 'Don’t enter account or routing numbers in the reference.';
    if (method === 'Check' && checkNumber.trim() && !CHECK_NUMBER.test(checkNumber.trim())) e.checkNumber = 'Use 1–20 letters, numbers or hyphens.';
    return e;
  }, [contractorId, paymentDate, method, serviceStart, serviceEnd, amount, memo, reference, checkNumber]);

  const docOk = doc === null || doc.state === 'ready' || doc.state === 'failed';
  const valid = Object.keys(errors).length === 0 && docOk && load.kind === 'loaded' && !busy;

  const touch = (f: Field) => setTouched((t) => (t.has(f) ? t : new Set(t).add(f)));
  const show = (f: Field) => (touched.has(f) ? errors[f] : undefined);

  function chooseContractor(id: string, list: ContractorResponse[] = contractors) {
    setContractorId(id);
    setError(null);
    const c = list.find((x) => x.id === id);
    if (c && !trackableTouched) setTrackable(c.form1099Configured);
  }

  async function takeFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setDoc({ file, state: 'checking', message: null });
    const result = await checkSupportingDocument(file);
    setDoc({ file, state: result.ok ? 'ready' : 'invalid', message: result.ok ? null : result.message });
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    void takeFile(e.dataTransfer.files[0]);
  }

  const FIELD_IDS: Record<Field, string> = {
    contractor: `${uid}-contractor`, paymentDate: `${uid}-date`, method: `${uid}-method`, serviceStart: `${uid}-sstart`, serviceEnd: `${uid}-send`, amount: `${uid}-amount`,
    memo: `${uid}-memo`, reference: `${uid}-ref`, checkNumber: `${uid}-checkno`,
  };
  const ORDER: Field[] = ['contractor', 'paymentDate', 'method', 'serviceStart', 'serviceEnd', 'amount', 'checkNumber', 'memo', 'reference'];
  const problems = ORDER.filter((f) => errors[f]);

  function focusField(f: Field) {
    const el = document.getElementById(FIELD_IDS[f]);
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
  }

  async function save(recordAnyway: boolean) {
    setTouched(new Set(['contractor', 'paymentDate', 'method', 'serviceStart', 'serviceEnd', 'amount', 'memo', 'reference', 'checkNumber']));
    if (Object.keys(errors).length > 0 || !docOk || !method) {
      // A Save attempt with something missing: show the summary and take the user to the first field that needs attention.
      setAttempted(true);
      const first = ORDER.find((f) => errors[f]);
      if (first) window.setTimeout(() => focusField(first), 0);
      else window.setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setAttempted(false);
    setBusy(true);
    setError(null);
    if (doc) setDoc({ ...doc, state: 'uploading', message: null });
    const result = await onSubmit({
      contractorId, paymentDate, paymentMethod: method, servicePeriodStart: serviceStart, servicePeriodEnd: serviceEnd, amount: Number(amount.trim()),
      memo, reference, checkNumber, form1099Trackable: trackable, recordAnyway, file: doc?.file ?? null,
    });
    if (result) {
      setError(result);
      if (doc) setDoc({ ...doc, state: 'failed', message: 'Not uploaded — nothing was saved. Try again.' });
      setBusy(false);
    }
  }

  const err = (f: Field) => (show(f) ? <p className="cp-field-error" id={`${uid}-${f}-err`} role="alert">{errors[f]}</p> : null);
  const invalid = (f: Field) => (show(f) ? true : undefined);

  return (
    <>
      <ModalDialog
        title="Record Outside Payment"
        size="md"
        className="cp-modal cp-sheet"
        busy={busy}
        onClose={onClose}
        footer={(
          <>
            <button type="button" className="cp-cancel" disabled={busy} onClick={onClose}>Cancel</button>
            <button type="button" className={`cp-save${valid ? '' : ' cp-save--inactive'}`} disabled={busy} aria-disabled={!valid} onClick={() => void save(false)}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        )}
      >
        {attempted && !valid && !busy && (
          <div className="cp-error-summary" role="alert" tabIndex={-1} ref={summaryRef} data-testid="outside-summary">
            <p><strong>{problems.length > 0 ? `Fix ${problems.length} ${problems.length === 1 ? 'field' : 'fields'} before saving:` : 'Finish the attachment before saving:'}</strong></p>
            <ul>
              {problems.map((f) => <li key={f}><button type="button" className="link" onClick={() => focusField(f)}>{errors[f]}</button></li>)}
              {!docOk && <li>{doc?.state === 'invalid' ? (doc.message ?? 'The attachment isn’t accepted.') : 'The attachment is still being checked.'} Remove it or choose another file.</li>}
            </ul>
          </div>
        )}
        <p className="cp-muted">Record a payment you already made outside Escochex, for history and 1099 review. This does not send, print or pay anything.</p>

        <div className="cp-form-field">
          <label htmlFor={`${uid}-contractor`}>Contractor Name <span className="cp-req" aria-hidden="true">*</span></label>
          {load.kind === 'error' ? (
            <p className="cp-field-error" role="alert">The contractors couldn’t be loaded. Close this window and try again.</p>
          ) : (
            <div className="cp-select cp-select--form">
              <select
                id={`${uid}-contractor`} data-autofocus value={contractorId} disabled={load.kind === 'loading'} aria-required="true" aria-invalid={invalid('contractor')}
                aria-describedby={show('contractor') ? `${uid}-contractor-err` : undefined} onBlur={() => touch('contractor')} onChange={(e) => chooseContractor(e.target.value)}
              >
                <option value="">{load.kind === 'loading' ? 'Loading contractors…' : 'Select a contractor'}</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.displayName}{c.businessName ? ` — ${c.businessName}` : ''}{c.isActive ? '' : ' (inactive)'}</option>)}
              </select>
              <span className="cp-select__caret" aria-hidden="true"><CaretDownIcon /></span>
            </div>
          )}
          {err('contractor')}
          <p className="cp-form-hint">
            <button type="button" className="link" onClick={() => setAdding(true)} disabled={busy}>Add contractor</button> <span className="cp-muted">if the contractor isn’t listed.</span>
          </p>
          {note && <p className="success" role="status">{note}</p>}
        </div>

        <div className="cp-form-row">
          <div className="cp-form-field">
            <label htmlFor={`${uid}-date`}>Payment Date <span className="cp-req" aria-hidden="true">*</span></label>
            <div className="cp-iconfield">
              <input id={`${uid}-date`} type="date" value={paymentDate} max={todayIso()} aria-required="true" aria-invalid={invalid('paymentDate')} onBlur={() => touch('paymentDate')} onChange={(e) => { setPaymentDate(e.target.value); setError(null); }} />
              <span className="cp-iconfield__icon" aria-hidden="true"><CalendarIcon /></span>
            </div>
            {err('paymentDate')}
          </div>
          <div className="cp-form-field">
            <label htmlFor={`${uid}-method`}>Payment Method <span className="cp-req" aria-hidden="true">*</span></label>
            <div className="cp-select cp-select--form">
              <select id={`${uid}-method`} value={method} aria-required="true" aria-invalid={invalid('method')} onBlur={() => touch('method')} onChange={(e) => { setMethod(e.target.value as PaymentMethod | ''); setError(null); }}>
                <option value="" disabled hidden />
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
              </select>
              <span className="cp-select__caret" aria-hidden="true"><CaretDownIcon /></span>
            </div>
            {err('method')}
          </div>
        </div>

        <fieldset className="cp-form-field cp-fieldset">
          <legend>Service Period <span className="cp-req" aria-hidden="true">*</span></legend>
          <div className="cp-range cp-range--form">
            <label className="cp-visually-hidden" htmlFor={`${uid}-sstart`}>Service period start</label>
            <input id={`${uid}-sstart`} type="date" value={serviceStart} max={serviceEnd || undefined} aria-required="true" aria-invalid={invalid('serviceStart')} onBlur={() => touch('serviceStart')} onChange={(e) => setServiceStart(e.target.value)} />
            <span aria-hidden="true">–</span>
            <label className="cp-visually-hidden" htmlFor={`${uid}-send`}>Service period end</label>
            <input id={`${uid}-send`} type="date" value={serviceEnd} min={serviceStart || undefined} aria-required="true" aria-invalid={invalid('serviceEnd')} onBlur={() => touch('serviceEnd')} onChange={(e) => setServiceEnd(e.target.value)} />
          </div>
          {err('serviceStart')}{err('serviceEnd')}
        </fieldset>

        <div className="cp-form-field">
          <label htmlFor={`${uid}-amount`}>Amount <span className="cp-req" aria-hidden="true">*</span></label>
          <input id={`${uid}-amount`} inputMode="decimal" placeholder="00.00" value={amount} aria-required="true" aria-invalid={invalid('amount')} onBlur={() => touch('amount')} onChange={(e) => { setAmount(e.target.value); setError(null); }} />
          {err('amount')}
        </div>

        {method === 'Check' && (
          <div className="cp-form-field">
            <label htmlFor={`${uid}-checkno`}>Check number (optional)</label>
            <input id={`${uid}-checkno`} value={checkNumber} maxLength={20} aria-invalid={invalid('checkNumber')} onBlur={() => touch('checkNumber')} onChange={(e) => setCheckNumber(e.target.value)} />
            <p className="cp-form-hint cp-muted">For your records only. It isn’t taken from Escochex’s check numbers.</p>
            {err('checkNumber')}
          </div>
        )}

        <div className="cp-form-field">
          <label htmlFor={`${uid}-memo`}>Memo / Notes</label>
          <textarea id={`${uid}-memo`} rows={3} maxLength={500} value={memo} aria-invalid={invalid('memo')} onBlur={() => touch('memo')} onChange={(e) => setMemo(e.target.value)} />
          {err('memo')}
        </div>

        <div className="cp-form-field">
          <label htmlFor={`${uid}-ref`}>Reference (optional)</label>
          <input id={`${uid}-ref`} value={reference} maxLength={100} placeholder="Invoice or transaction note" aria-invalid={invalid('reference')} onBlur={() => touch('reference')} onChange={(e) => { setReference(e.target.value); setError(null); }} />
          {err('reference')}
        </div>

        <div className="cp-form-field">
          <span className="cp-label" id={`${uid}-docs`}>Supporting Documents</span>
          <div
            className={`cp-drop${dragging ? ' cp-drop--active' : ''}`} role="group" aria-labelledby={`${uid}-docs`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
          >
            <UploadIcon />
            <p>
              <button type="button" className="link cp-drop__pick" disabled={busy} onClick={() => fileInput.current?.click()}>Click to upload</button> or drag and drop
            </p>
            <p className="cp-drop__hint">PDF, JPG, PNG · Max 10 MB</p>
            <input ref={fileInput} type="file" className="cp-visually-hidden" tabIndex={-1} aria-label="Supporting document file" accept={ACCEPT_ATTR} onChange={(e) => { void takeFile(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          {doc && (
            <div className="cp-files" aria-live="polite">
              <p className="cp-files__title">Uploaded Files (1):</p>
              <div className={`cp-chip cp-chip--${doc.state}`} data-testid="file-chip">
                <FileIcon />
                <div className="cp-chip__text">
                  <span className="cp-chip__name">{doc.file.name}</span>
                  <span className="cp-chip__state">
                    {doc.state === 'checking' && 'Checking…'}
                    {doc.state === 'ready' && `Ready · ${formatBytes(doc.file.size)} · uploads when you save`}
                    {doc.state === 'uploading' && `Uploading… · ${formatBytes(doc.file.size)}`}
                    {doc.state === 'invalid' && (doc.message ?? 'Not accepted')}
                    {doc.state === 'failed' && (doc.message ?? 'Not uploaded')}
                  </span>
                </div>
                <button type="button" className="cp-chip__remove" aria-label={`Remove ${doc.file.name}`} disabled={busy} onClick={() => setDoc(null)}><TrashIcon /></button>
              </div>
            </div>
          )}
        </div>

        <div className="cp-toggle-row">
          <button
            type="button" role="switch" id={`${uid}-trackable`} aria-checked={trackable} className={`cp-switch${trackable ? ' cp-switch--on' : ''}`}
            onClick={() => { setTrackable((v) => !v); setTrackableTouched(true); }} aria-label="1099 Trackable"
          ><span className="cp-switch__knob" /></button>
          <label htmlFor={`${uid}-trackable`}>1099 Trackable</label>
          {selected && !trackableTouched && <span className="cp-muted cp-toggle-note">{selected.form1099Configured ? 'Set from the contractor’s 1099 setup.' : 'Contractor isn’t set up for 1099.'}</span>}
        </div>

        {error && (
          <div className="cp-form-error" role="alert" data-testid="outside-error">
            <p>{error.message}</p>
            {error.duplicate && <button type="button" className="cp-btn cp-btn--mini" disabled={busy} onClick={() => void save(true)}>Record anyway</button>}
          </div>
        )}
      </ModalDialog>
      {adding && (
        <AddContractorDialog
          defaultPayMethod={null}
          className="cp-sheet"
          onClose={() => setAdding(false)}
          onCreated={(created) => {
            const next = [...contractors, created];
            setLoad({ kind: 'loaded', contractors: next });
            chooseContractor(created.id, next);
            setNote(`${created.displayName} was added and selected.`);
            setAdding(false);
          }}
        />
      )}
    </>
  );
}
